import { useState } from "react";
import { StoreLayout } from "../../components/StoreLayout";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { CheckCircle, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";

const SERVICES = [
  "AC Repair & Service", "AC Installation", "Refrigerator Repair",
  "Washing Machine Repair", "TV Repair & Service", "Home Cleaning",
  "Pest Control", "Other",
];

export default function BookServicePublic() {
  const [form, setForm] = useState({
    name: "", phone: "", service_required: "", area: "", pincode: "",
    preferred_date: "", preferred_time: "", details: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your name"); return; }
    if (!/^[6-9]\d{9}$/.test(form.phone.trim())) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (!form.service_required) { toast.error("Please select a service"); return; }
    if (!form.area.trim()) { toast.error("Please enter your area/locality"); return; }
    if (!/^\d{6}$/.test(form.pincode.trim())) { toast.error("Enter a valid 6-digit pincode"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/service-request", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service_required: form.service_required,
        area: form.area.trim(),
        pincode: form.pincode.trim(),
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        details: form.details.trim(),
      });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <StoreLayout>
        <div className="max-w-xl mx-auto px-5 py-16 text-center" data-testid="book-service-success">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle size={44} weight="fill" className="text-green-600" />
          </div>
          <h1 className="font-[Outfit] text-3xl font-extrabold tracking-tight mt-6">
            Request Received! 🎉
          </h1>
          <p className="text-[#475569] mt-3">Thank you for contacting Fixigo Home Services.</p>
          <p className="text-[#475569] mt-1">Our team will contact you shortly.</p>
          {done.lead_id && (
            <p className="mt-5 inline-block bg-[#eef2ff] border border-[#c7d2fe] text-[#4F46E5] font-semibold rounded-lg px-4 py-2 text-sm"
               data-testid="book-service-lead-id">
              Lead ID: {done.lead_id}
            </p>
          )}
          <Button
            className="rounded-xl mt-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold"
            onClick={() => (window.location.href = "/")}
            data-testid="book-service-back-home"
          >
            Back to Home
          </Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="bg-gradient-to-br from-[#1D4ED8] to-[#2563EB] text-white">
        <div className="max-w-2xl mx-auto px-5 py-10 sm:py-12">
          <div className="flex items-center gap-2 text-[#BFDBFE]">
            <Wrench size={22} weight="fill" />
            <span className="uppercase text-xs tracking-[0.2em] font-semibold">Fixigo Home Services</span>
          </div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
            Book Your Home Service
          </h1>
          <p className="text-white/85 mt-2 text-sm sm:text-base">
            Tell us what you need and our Fixigo team will get back to you.
          </p>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-5 py-6 sm:py-8">
        <form
          onSubmit={submit}
          className="bg-white border border-[#E2E8F0] rounded-2xl p-5 sm:p-7 space-y-5"
          data-testid="book-service-form"
        >
          <div>
            <Label className="label-caps">Name <span className="text-red-500">*</span></Label>
            <Input value={form.name} onChange={upd("name")} placeholder="Your full name"
              className="rounded-lg mt-1.5" data-testid="bs-name" required />
          </div>

          <div>
            <Label className="label-caps">Mobile Number <span className="text-red-500">*</span></Label>
            <Input value={form.phone} onChange={upd("phone")} type="tel" inputMode="numeric"
              maxLength={10} placeholder="10-digit mobile number"
              className="rounded-lg mt-1.5" data-testid="bs-phone" required />
          </div>

          <div>
            <Label className="label-caps">Service Required <span className="text-red-500">*</span></Label>
            <Select value={form.service_required} onValueChange={(v) => setForm({ ...form, service_required: v })}>
              <SelectTrigger className="rounded-lg mt-1.5" data-testid="bs-service">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`bs-service-${s.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-caps">Area / Locality <span className="text-red-500">*</span></Label>
              <Input value={form.area} onChange={upd("area")} placeholder="e.g. Kondapur, HSR Layout"
                className="rounded-lg mt-1.5" data-testid="bs-area" required />
            </div>
            <div>
              <Label className="label-caps">Pincode <span className="text-red-500">*</span></Label>
              <Input value={form.pincode} onChange={upd("pincode")} type="tel" inputMode="numeric"
                maxLength={6} placeholder="6-digit pincode"
                className="rounded-lg mt-1.5" data-testid="bs-pincode" required />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-caps">Preferred Date</Label>
              <Input type="date" value={form.preferred_date} onChange={upd("preferred_date")}
                className="rounded-lg mt-1.5" data-testid="bs-date" />
            </div>
            <div>
              <Label className="label-caps">Preferred Time</Label>
              <Input type="time" value={form.preferred_time} onChange={upd("preferred_time")}
                className="rounded-lg mt-1.5" data-testid="bs-time" />
            </div>
          </div>

          <div>
            <Label className="label-caps">Requirement Details</Label>
            <Textarea value={form.details} onChange={upd("details")}
              placeholder="Describe the issue or requirement (optional)"
              className="rounded-lg mt-1.5" rows={3} data-testid="bs-details" />
          </div>

          <Button
            type="submit"
            className="rounded-xl w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold"
            disabled={busy}
            data-testid="bs-submit"
          >
            {busy ? "Submitting…" : "Submit Service Request"}
          </Button>
        </form>
      </div>
    </StoreLayout>
  );
}
